<script setup>
import {getCurrentInstance, onBeforeUnmount, reactive, ref, watch} from 'vue'
import {
    Feedback,
    Progress,
    State,
    delay,
    getModels,
    expand,
    expressionAsync,
    go,
    waitRender,
    clone,
} from '@/paks/vu-app'
import AudioWidget from './AudioWidget.vue'
import ButtonWidget from './ButtonWidget.vue'
import EventWidget from './EventWidget.vue'
import GaugeWidget from './GaugeWidget.vue'
import FormWidget from './FormWidget.vue'
import IconWidget from './IconWidget.vue'
import ImageWidget from './ImageWidget.vue'
import InfoWidget from './InfoWidget.vue'
import InputWidget from './InputWidget.vue'
import LedWidget from './LedWidget.vue'
import LineWidget from './LineWidget.vue'
import MetricTableWidget from './MetricTableWidget.vue'
import ProgressWidget from './ProgressWidget.vue'
import SignWidget from './SignWidget.vue'
import ShapeWidget from './ShapeWidget.vue'
import SpeedometerWidget from './SpeedometerWidget.vue'
import TableWidget from './TableWidget.vue'
import TabsWidget from './TabsWidget.vue'
import ToolbarWidget from './ToolbarWidget.vue'
import ValueWidget from './ValueWidget.vue'
import VideoWidget from './VideoWidget.vue'
import Json5 from 'json5'

let {proxy: self} = getCurrentInstance()

const GridMin = 20
const GridSize = 20

const props = defineProps({
    // TODO - rename board
    dashboard: Object,
    design: Boolean,
    value: Number | Object,
    widget: Object,
})

defineExpose({update})

const emit = defineEmits(['add', 'click', 'edit', 'remove', 'resize'])

const page = reactive({
    canEdit: null,
    dark: null,
    footer: null,
    framed: null,
    header: null,
    mirror: null,
    pos: null,
    resizing: false,
    title: null,
    widgets: null,
})

//  Component refs
const resizeHandle = ref(null)
const widgetHeaders = ref(null)
const cardRef = ref(null)
const widgetRef = ref(null)
const header = ref(null)
const footer = ref(null)
const title = ref(null)

let listeners = []
let lastAction = new Date()

onBeforeUnmount(() => {
    removeListeners()
})

async function update(params) {
    let widget = props.widget

    page.framed = widget.framed != null ? widget.framed : props.dashboard.framed
    page.canEdit = props.design
    page.dark = State.app.dark ? 'dark' : 'light'
    if (widgetRef.value) {
        await setWidgetStyles()

        page.footer = getContent(widget, footer.value, widget.footer)
        page.header = getContent(widget, header.value, widget.header)
        page.title = getContent(widget, title.value, widget.title)

        if (widgetRef.value?.update) {
            await widgetRef.value?.update(params)
        }
    }
    if (props.design && resizeHandle.value) {
        addListener(resizeHandle.value.$el, 'mousedown', startResize)
        addListener(resizeHandle.value.$el, 'touchstart', startResize)
    }
}

/*
    Apply style overrides for header, footer and title content and expand ${vars}
 */
function getContent(widget, el, value) {
    if (el?.style) {
        value = el.style[`--w-content`] || value
    }
    return expand(value, {value: widget.value, inputs: State.context.inputs})
}

function bindWidget(type) {
    for (let [key, component] of Object.entries(State.ref.widgets)) {
        if (key == type) {
            return component
        }
    }
    console.error(`Cannot find widget type ${type}`)
    return null
}

function removeStyles(widget, el) {
    if (widget._removedCss) {
        for (let prop of widget._removedCss) {
            el.style[prop] = null
            if (prop == 'background') {
                let cel = cardRef.value
                if (cel) {
                    cel.$el.style.backgroundColor = null
                }
            }
        }
        delete widget._removedCss
    }
}

async function setWidgetStyles() {
    let el = widgetRef.value?.$el
    let widget = props.widget
    widget._appliedStyles = {}
    removeStyles(widget, el)
    if (props.dashboard.widgetCss) {
        await setStyles(widget, el, props.dashboard.widgetCss)
    }
    await setStyles(widget, el, props.widget.css)
    await waitRender()
}

async function setStyles(widget, el, css) {
    if (!el) return
    /*
        [expression:][path:]property = value
        Paths: element,.class#id,element.class#id.class#id
     */
    for (let item of css) {
        let {expr, path, property, value} = item
        try {
            let enabled = await expressionAsync(expr, {
                context: {value: widget.value, inputs: State.context.inputs},
            })
            patchStyle(widget, el, path, property, value, enabled)
        } catch (err) {
            console.log('Bad CSS', err.message, item)
        }
    }
}

/*
    Patch styles using the users CSS properties
    Patch the "path" element where path is a DOM path consisting of elements, classes or IDs. 
    Followed by a dot and a property name. 
    Examples:
        component.footer.background
        .primary#icon.background
        component,element.background

    Value is the CSS property value, enabled is the condition result or widget.value if no condition
    The value can be "trueValue:falseValue" and the appropriate value is picked depending on "enabled".
    If the path ends with "class" then we must remove the class if disabled.

    El is set to the widget element.
 */
function patchStyle(widget, el, path, property, value, enabled) {
    if (path) {
        let parts = path.match(/([.,#]?[^.,#]+)/g) || []
        if (parts[0] == '.primary') {
            // el is the primary so skip it
            parts = parts.slice(1)
        }
        parts = parts.map((p) => p.replace(/^,/, ''))
        for (let part of parts) {
            let nel = el.querySelector(part)
            if (!nel) {
                // Step up levels to find the header, footer or title
                el = el.closest('.widget').querySelector(part)
            } else {
                el = nel
            }
            if (!el) {
                return
            }
        }
    }
    /*
        If the CSS value is a colon-separated pair, then pick the appropriate value based on 
        the value of the expression (enabled).
     */
    let values
    if (Array.isArray(value)) {
        values = value
        value = enabled ? values[0] : values[1]
    } else {
        // REPAIR (remove this)
        values = value.toString().trim().split(':')
        if (values.length > 1) {
            value = enabled ? values[0] : values[1]
        }
    }

    if (property == 'class') {
        //  TODO DOC
        if (values) {
            //  Remove both true:false classes
            for (let c of values) {
                el.classList.remove(c)
            }
            el.classList.add(values)
            return
        }
    }
    if (Array.isArray(value) && value.length == 1 && !enabled) {
        return
    }
    if (el?.style) {
        el.style[property] = value
    }
    let key = (path ? path : '.primary') + '.' + property
    widget._appliedStyles[key] = value

    if (property == 'color') {
        //  Apply top-level prop to widget headers too
        if (title.value) {
            title.value.$el.style[property] = value
        }
        if (widgetHeaders.value) {
            widgetHeaders.value.style[property] = value
        }
    }
}

function startResize(e) {
    e.stopPropagation()
    let element = self.$el

    addListener(document, 'mousemove', resize)
    addListener(document, 'mouseup', finishResize)
    addListener(document, 'touchmove', resize)
    addListener(document, 'touchend', finishResize)

    let mirror = (page.mirror = element.cloneNode(true))
    mirror.id = props.widget.id + '-mirror'

    let pos = (page.pos = getPos(e, element))
    mirror.style.width = align(pos.width) + 'px'
    mirror.style.height = align(pos.height) + 'px'

    mirror.style.top = element.offsetTop + 'px'
    mirror.style.left = element.offsetLeft + 'px'
    mirror.className += ' resizing-widget'
    mirror.style.zIndex = 1000
    mirror.style.opacity = 0.5

    element.parentElement.appendChild(mirror)
    notifyResizing('start-resize', pos)
    page.resizing = true
}

function resize(e) {
    if (!page.resizing) return
    let mirror = page.mirror
    e.stopPropagation()
    let pos = (page.pos = getPos(e, mirror))
    let width = align(pos.width)
    let height = align(pos.height)
    mirror.style.width = width + 'px'
    mirror.style.height = height + 'px'
    notifyResizing('resize', pos)
    if (resizeHandle.value) {
        resizeHandle.value.$el.style.cursor = 'nwse-resize'
    }
}

async function finishResize(e) {
    if (page.resizing) {
        let mirror = page.mirror
        e.stopPropagation()
        let pos = getPos(e, mirror)
        pos.width = align(pos.width)
        pos.height = align(pos.height)
        notifyResizing('finish-resize', pos)
        if (resizeHandle.value) {
            resizeHandle.value.$el.style.cursor = ''
        }
        removeListeners(['mousemove', 'mouseup', 'touchmove', 'touchend'])
        await waitRender()
        let stopWatcher = watch(
            () => State.app.needs.widgetsRendered,
            () => {
                self.$el.parentElement.removeChild(mirror)
                stopWatcher()
                State.app.noNeed('widgetsRendered')
            }
        )
    }
}

function getPos(e, widget) {
    let bounds = widget.getBoundingClientRect()
    let {left, top} = bounds
    let width, height
    if (e.touches) {
        if (e.touches.length) {
            width = e.touches[0].clientX - bounds.left
            height = e.touches[0].clientY - bounds.top
        } else {
            width = page.pos.width
            height = page.pos.height
        }
    } else {
        width = e.clientX - bounds.left
        height = e.clientY - bounds.top
    }
    return {height, width, left, top, bounds}
}

async function editWidget() {
    emit('edit', props.widget)
}

async function removeWidget() {
    emit('remove', props.widget)
}

function notifyResizing(cmd, pos) {
    emit('resize', {
        cmd: cmd,
        id: props.widget.id,
        height: pos.height,
        width: pos.width,
    })
}

function addListener(base, event, fn) {
    listeners.push({base, event, fn})
    base.addEventListener(event, fn)
}

function removeListeners(events) {
    for (let listener of listeners) {
        if (!events || events.indexOf(listener.event) >= 0) {
            listener.base.removeEventListener(listener.event, listener.fn)
        }
    }
    listeners = []
}

function align(v) {
    if (props.dashboard.layout == 'grid') {
        return Math.max(Math.round(v / GridSize) * GridSize, GridMin)
    }
    return v
}

function rightClick(e) {
    e.preventDefault()
    State.app.setNeed('editWidget', props.widget)
    return false
}

async function widgetClicked(e) {
    let widget = props.widget

    if (widget._moved && widget._moved > Date.now() - 1000) {
        return
    }
    if (widgetRef.click) {
        // Pass click event down to the widget
        widgetRef.click(e)
    } else if (widgetRef.value?.click) {
        widgetRef.value.click(e)
    }
    if (widgetRef.value?.page && widget.input != 'form') {
        widgetRef.value.page.saving = true
    }
    if (!(widget.type == 'input' || widget.type == 'toolbar' || widget.type == 'table' || widget.type == 'metric')) {
        await run(widget)
    }
    if (widgetRef.value?.page && widget.input != 'form') {
        widgetRef.value.page.saving = false
    }
}

/*
    Form input handler
 */
async function formInput(widget) {
    if (!widget || !widget.form) return
    let fields = {}

    Progress.start('medium')
    if (widgetRef.value?.page) {
        widgetRef.value.page.saving = true
    }
    /*
        Gather form field input
     */
    if (widget.actions) {
        for (let w of props.dashboard.widgets) {
            if (w.form == widget.form && w.input != 'form') {
                let name = w.formField || w.label?.toLowerCase()
                if (name) {
                    fields[name] = w.value // getProperty(widget.value, name)
                }
            }
        }
        widget.value = fields
        if (widget.actions) {
            await run(widget)
        }
    } else {
        for (let w of props.dashboard.widgets) {
            if (w.form == widget.form && w.input != 'form' && w.actions) {
                await run(w)
            }
        }
    }
    State.app.setNeed('refreshData')
    if (widgetRef.value?.page) {
        widgetRef.value.page.saving = false
    }
    Progress.stop()
}

/*
    Provided function to be called from inputWidget and formWidget
 */
async function widgetInput() {
    let widget = props.widget
    Progress.start('short')
    if (widget.actions) {
        if (widget.type == 'input' && widget.input == 'slider') {
            // Debounce slider input
            if (widget._timeout) {
                clearTimeout(widget._timeout)
            }
            widget._timeout = setTimeout(async () => {
                await run(widget)
            }, 250)
        } else {
            await run(widget)
        }
        State.app.setNeed('refreshData')
    }
    Progress.stop()
}

/*
    Get a property from a nested object
 */
function getProperty(result, path) {
    if (!path) {
        return result
    }
    let parts = path.split('.').slice(0, -1)
    if (parts.length > 0) {
        for (let prop of parts) {
            if (result == null) {
                break
            }
            result = result[prop]
        }
        return result
    }
}

async function run(widget) {
    for (let action of widget.actions || []) {
        let {type, target, conditions} = action
        if (!target) return

        let {Dashboard} = getModels()
        if (type == 'dashboard') {
            await Dashboard.setByName(target)
            State.app.setNeed('reload')
        } else if (type == 'link') {
            go(target)
        } else if (type == 'action') {
            conditions = conditions || [{expression: true, params: {}}]
            await runAction(widget, conditions, target)
        }
    }
}

async function runAction(widget, conditions, target) {
    let {Action} = getModels()
    for (let i = 0; i < 30 && widget._loading; i++) {
        await delay(100)
    }
    widget._loading = true
    await waitRender()

    for (let cond of conditions) {
        if (cond.expression) {
            if (!(await expressionAsync(cond.expression, {context: {value: widget.value}}))) {
                continue
            }
        }
        if (cond.params) {
            let context = clone(cond.params)
            try {
                if (context[0] == '{') context = Json5.parse(context) /*}*/
                context = Object.assign(
                    {
                        deviceId: State.context.deviceId,
                    },
                    context
                )
                if (widget.input == 'form') {
                    Object.assign(context, widget.value)
                } else if (widget.type == 'input') {
                    context[widget.formField || 'value'] = widget.value
                }
            } catch (err) {
                console.log(`Cannot parse JSON`, context)
            }
            try {
                // Slow down if too many actions in a row
                if (lastAction.getTime() > Date.now() - 500) {
                    await delay(500)
                }
                await Action.invoke({name: target, context})
                lastAction = new Date()

                //  Apply input value to context so refresh will get the value immediately
                if (widget.type == 'input') {
                    if (widget.input == 'form') {
                        for (let [key, value] of Object.entries(context)) {
                            State.context.inputs[key] = value
                        }
                    } else {
                        if (widget.model == 'Store' && widget.dimensions.key) {
                            State.context.inputs[widget.dimensions.key] = widget.value
                        } else if (widget.formField) {
                            State.context.inputs[widget.formField] = widget.value
                        }
                    }
                }
            } catch (err) {
                Feedback.error(err.message)
            }
            break
        }
    }
    widget._loading = false
}
</script>

<template>
    <div
        class="widget"
        :id="widget.id"
        :class="`widget-${widget.type}` + (widget.input ? 'input-' + widget.input : '')">
        <v-card
            v-show="!widget._hide"
            ref="cardRef"
            class="widget-card card"
            :class="`widget-${widget.type} ${widget.zoomed || ''} 
                ${page.framed ? 'framed' : 'flat'}
                ${props.design ? 'design' : 'fixed'}
                ${widget._error ? 'error' : ''}
            `"
            :hover="false"
            :elevation="0"
            :ripple="widget.type == 'input' || widget.type == 'table' ? false : true"
            @click.alt="rightClick"
            @contextmenu.prevent.stop="rightClick"
            @click="widgetClicked">
            <v-card-title ref="title" class="widget-title" v-if="page.title && page.framed">
                {{ page.title }}
            </v-card-title>
            <v-card-text class="">
                <div v-if="widget._error" class="error" align="center" justify="center">
                    Widget Error: {{ widget._error }}
                </div>
                <div
                    v-else-if="
                        widget.value == null &&
                        widget.text == null &&
                        widget.defaultValue == null &&
                        widget.type != 'input' &&
                        (widget.metric || widget.model)
                    "
                    class="nodata"
                    align="center"
                    justify="center">
                    Waiting for data...
                </div>
                <AudioWidget
                    v-else-if="widget.type == 'audio'"
                    :dashboard="dashboard"
                    :widget="widget"
                    :state="State"
                    ref="widgetRef" />
                <ButtonWidget
                    v-else-if="widget.type == 'button' || (widget.type == 'input' && widget.input == 'button')"
                    :dashboard="dashboard"
                    :widget="widget"
                    :state="State"
                    ref="widgetRef" />
                <EventWidget
                    v-else-if="widget.type == 'event'"
                    :dashboard="dashboard"
                    :widget="widget"
                    :state="State"
                    ref="widgetRef" />
                <FormWidget
                    v-else-if="widget.type == 'input' && widget.input == 'form'"
                    :dashboard="dashboard"
                    :widget="widget"
                    :state="State"
                    ref="widgetRef"
                    @submit="formInput" />
                <GaugeWidget
                    v-else-if="widget.type == 'gauge'"
                    :dashboard="dashboard"
                    :widget="widget"
                    :state="State"
                    ref="widgetRef" />
                <SpeedometerWidget
                    v-else-if="widget.type == 'speedometer'"
                    :dashboard="dashboard"
                    :widget="widget"
                    :state="State"
                    ref="widgetRef" />
                <IconWidget
                    v-else-if="widget.type == 'input' && widget.input == 'icon'"
                    :dashboard="dashboard"
                    :widget="widget"
                    :state="State"
                    ref="widgetRef" />
                <ImageWidget
                    v-else-if="widget.type == 'image'"
                    :dashboard="dashboard"
                    :widget="widget"
                    :state="State"
                    ref="widgetRef" />
                <InfoWidget
                    v-else-if="widget.type == 'info'"
                    :dashboard="dashboard"
                    :widget="widget"
                    :state="State"
                    ref="widgetRef" />
                <InputWidget
                    v-else-if="widget.type == 'input' && widget.input != 'form' && widget.input != 'button'"
                    :dashboard="dashboard"
                    :widget="widget"
                    :state="State"
                    ref="widgetRef"
                    @submit="widgetInput" />
                <LedWidget
                    v-else-if="widget.type == 'led'"
                    :dashboard="dashboard"
                    :widget="widget"
                    :state="State"
                    ref="widgetRef" />
                <LineWidget
                    v-else-if="widget.type == 'line'"
                    :dashboard="dashboard"
                    :widget="widget"
                    :state="State"
                    ref="widgetRef" />
                <MetricTableWidget
                    v-else-if="widget.type == 'metric'"
                    :dashboard="dashboard"
                    :widget="widget"
                    :state="State"
                    ref="widgetRef" />
                <ProgressWidget
                    v-else-if="widget.type == 'progress'"
                    :dashboard="dashboard"
                    :widget="widget"
                    :state="State"
                    ref="widgetRef" />
                <SignWidget
                    v-else-if="widget.type == 'sign'"
                    :dashboard="dashboard"
                    :widget="widget"
                    :state="State"
                    ref="widgetRef" />
                <ShapeWidget
                    v-else-if="widget.type == 'shape'"
                    :dashboard="dashboard"
                    :widget="widget"
                    :state="State"
                    ref="widgetRef" />
                <TableWidget
                    v-else-if="widget.type == 'table'"
                    :dashboard="dashboard"
                    :widget="widget"
                    :state="State"
                    ref="widgetRef" />
                <TabsWidget
                    v-else-if="widget.type == 'tabs'"
                    :dashboard="dashboard"
                    :widget="widget"
                    :state="State"
                    ref="widgetRef" />
                <ValueWidget
                    v-else-if="widget.type == 'numeric' || widget.type == 'label' || widget.type == 'text'"
                    :dashboard="dashboard"
                    :widget="widget"
                    :state="State"
                    ref="widgetRef" />
                <ToolbarWidget
                    v-else-if="widget.type == 'toolbar'"
                    :board="dashboard"
                    :widget="widget"
                    :state="State"
                    ref="widgetRef" />
                <VideoWidget
                    v-else-if="widget.type == 'video'"
                    :board="dashboard"
                    :widget="widget"
                    :state="State"
                    ref="widgetRef" />
                <component
                    v-else
                    v-if="bindWidget(widget.type)"
                    :is="bindWidget(widget.type)"
                    :dashboard="dashboard"
                    :widget="widget"
                    :state="State"
                    ref="widgetRef" />
            </v-card-text>

            <div
                class="widget-headers"
                ref="widgetHeaders"
                v-if="
                    widget.type != 'tabs' &&
                    widget.type != 'input' &&
                    widget.type != 'metric' &&
                    widget.type != 'sign' &&
                    widget.type != 'toolbar' &&
                    widget.type != 'info'
                ">
                <div ref="header" class="header" :class="`header-${widget.type} ${page.dark}`">
                    {{ page.header }}
                </div>
                <div ref="footer" class="footer" :class="`footer-${widget.type} ${page.dark}`">
                    {{ page.footer }}
                </div>
            </div>
            <div v-if="widget.shadow === true" class="shadow">
                <v-icon icon="$close" size="large" />
            </div>
        </v-card>

        <div v-if="page.canEdit && !widget.fixed" class="manage-widget" :class="widget.zoomed">
            <v-icon
                v-if="!widget.zoomed"
                :class="`manage-icon pr-1 ${page.dark}`"
                icon="$edit"
                @mousedown.stop="editWidget"
                @touchstart.stop="editWidget" />
        </div>
        <v-icon
            icon="$resize"
            v-if="page.canEdit && !widget.zoomed && widget.type != '__toolbar'"
            class="resize-handle"
            :class="props.design ? 'design' : ''"
            ref="resizeHandle" />
    </div>
</template>

<style lang="scss" scoped>
.widget {
    border-radius: 0;
    .widget-card {
        height: 100%;
        width: 100%;
        display: flex;
        border: solid 1px rgb(var(--v-theme-border));
        border-radius: 8px;
        background: transparent;
        flex-direction: column;
        overflow-y: scroll;

        box-shadow: none !important;
        transition: none !important;

        .v-card-title {
            text-align: center;
            line-height: 1.2;
        }
        .v-card-text {
            container-type: size;
            display: flex;
            flex-grow: 1;
            height: 100%;
            width: 100%;
            padding: 0;
            user-select: none;
            background: inherit;
            pointer-events: auto;
        }
        .nodata {
            position: absolute;
            top: 45%;
            width: 100%;
            text-align: center;
            font-size: 1.2rem;
            color: rgb(var(--v-theme-surface-lighten-2));
        }
        .error {
            position: absolute;
            top: 45%;
            width: 100%;
            text-align: center;
            font-size: 1.2rem;
            border-color: 2px solid red;
        }
    }
    .widget-card.error {
        border: 2px solid red !important;
    }
    .widget-card.framed {
        background: rgb(var(--v-theme-background-lighten-1));
    }
    .widget-card.framed:not(.error) {
        border-color: rgba(var(--v-theme-accent-darken-2), 0.5);
    }
    .widget-card.flat:not(.error) {
        box-shadow: none !important;
        border-radius: 0 !important;
        border: none !important;
    }
    .widget-card.zoomed {
        border-radius: 0 !important;
        border: none !important;
    }
    .widget-card.design {
        cursor: move;
    }
    .widget-toolbar {
        background: inherit;
        z-index: 10;
    }
    .manage-widget {
        position: absolute;
        text-align: right;
        top: 0px;
        right: 0px;
        height: 26px;
        width: 44px;
        z-index: 12;
        cursor: pointer;
        opacity: 0;
        @media (max-width: 640px) {
            opacity: 0.5;
        }
        .manage-icon {
            font-size: 1.8rem;
            color: rgb(var(--v-theme-primary-lighten-1));
            opacity: 0.75;
            filter: drop-shadow(2px 2px 2px rgba(0, 0, 0, 0.5));
        }
        .manage-icon.dark {
            color: white;
        }
    }
    .manage-widget:hover {
        opacity: 1;
    }
    .move-widget:hover + .manage-widget {
        opacity: 1;
    }
    .resize-handle {
        position: absolute !important;
        color: rgb(var(--v-theme-border));
        bottom: 0;
        right: 0;
        opacity: 0;
        z-index: 12;
    }
    .resize-handle:hover {
        cursor: nwse-resize;
        opacity: 1;
    }
    .manage-widget.zoomed {
        display: block;
        height: 24px !important;
        width: 24px !important;
        right: 6px !important;
    }
    .zoomed {
        border: none !important;
        border-radius: 0 !important;
    }
    .widget-headers {
        height: 100%;
        width: 100%;
        container-type: size;
        position: absolute;
        text-align: center;
        .header {
            position: relative;
            top: 6cqh;
            width: 100%;
            font-size: max(16px, min(min(6cqh, 6cqw), 68px));
            font-weight: normal;

            // Like info headers
            text-transform: uppercase;
            color: rgb(var(--v-theme-text));
            opacity: 0.75;
            text-align: left;
            padding: 0 16px 0 16px;
            letter-spacing: 0.02em;
        }
        .header-form {
            padding-left: 10px;
            text-align: left;
        }
        .header-gauge {
            top: 4cqh;
        }
        .header-graph {
            top: 4cqh;
        }
        .header-table {
            top: 4cqh;
        }
        .footer {
            position: absolute;
            bottom: 4cqh;
            text-align: center;
            width: 100%;
            opacity: 0.75;
            font-size: max(12px, min(min(4cqh, 4cqw), 48px));
            font-weight: normal;
            color: inherit;
        }
        .footer-graph {
            top: 92cqh;
        }
        pointer-events: none;
    }
    .shadow {
        container-type: size;
        position: absolute;
        top: 0;
        left: 0;
        height: 100%;
        width: 100%;
        background: #888888;
        opacity: 50%;
        pointer-events: fill;
        cursor: move;
        z-index: 2;
        .v-icon {
            position: relative;
            top: 50cqh;
            left: 50cqw;
            transform: translate(-50%, -50%);
            display: block;
            width: 80cqw;
            height: 80cqh;
        }
    }
}
.input-date {
    z-index: 150 !important;
    .v-card {
        overflow: visible;
        padding: 2px;
    }
}
.resizing-widget {
    position: absolute;
    .v-card-actions {
        .v-btn {
            margin-right: 8px !important;
            background-color: rgb(var(--v-theme-accent)) !important;
            border-color: rgb(var(--v-theme-accent)) !important;
        }
    }
}
</style>
